<template>
  <div class="scene-image" aria-label="Scene Image" data-testid="scene-image-container">
    <img
      aria-label="Environment image"
      data-testid="environment-image"
      class="environment-img"
      :src="environmentUrl(location)"
    >
    <div v-if="npcAvatarKey">
      <div v-if="layerOutfit(npcOutfit)">
        <img
          v-if="npcReady"
          aria-label="NPC image"
          data-testid="npc-image"
          class="npc-img right-shifted"
          :src="npcBaseSrc"
        >
        <img
          v-if="npcReady"
          aria-label="NPC outfit image"
          data-testid="npc-outfit-layer-image"
          class="npc-outfit-img right-shifted"
          :src="npcClothingSrc"
        >
      </div>
      <img
        v-else
        aria-label="NPC image"
        data-testid="npc-with-outfit-image"
        class="npc-outfit-img right-shifted"
        :src="npcBaseSrc"
      >
    </div>

    <div v-if="layerOutfit(outfit)">
      <img
        v-if="patientReady"
        aria-label="Patient image"
        data-testid="patient-image"
        class="patient-img"
        :class="{ centered: !npcAvatarKey, 'left-shifted': npcAvatarKey }"
        :src="patientBaseSrc"
      >
      <img
        v-if="patientReady"
        aria-label="Outfit image"
        data-testid="patient-outfit-layer-image"
        class="outfit-img"
        :class="{ centered: !npcAvatarKey, 'left-shifted': npcAvatarKey }"
        :src="patientClothingSrc"
      >
    </div>
    <img
      v-else
      aria-label="Patient image"
      data-testid="patient-with-outfit-image"
      class="patient-img"
      :class="{ centered: !npcAvatarKey, 'left-shifted': npcAvatarKey }"
      :src="patientBaseSrc"
    >
  </div>
</template>

<script>
import get from 'lodash/get';
import { outfitHasColor } from '~lib/common/utils/color';

export default {
  name: 'scene-image',
  props: {
    avatars: {
      type: Array,
      default: () => []
    },
    location: {
      type: Object,
      required: true
    },
    avatarKey: {
      type: String,
      required: true
    },
    outfit: {
      type: Object,
      default: () => {}
    },
    npcAvatarKey: {
      type: String,
      default: null
    },
    npcOutfit: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      patientReady: false, // not true until both base and clothing images have loaded
      npcReady: false, // not true until both base and clothing images have loaded
    }
  },
  computed: {
    patientBaseSrc() {
      return this.patientUrl(this.avatarKey, this.outfit);
    },
    patientClothingSrc() {
      return this.outfitUrl(this.patientAvatar, this.outfit.clothingType, this.outfit.color);
    },
    patientAvatar() {
      return this.avatars.find(a => a.key === this.avatarKey);
    },
    npcBaseSrc() {
      return this.patientUrl(this.npcAvatarKey, this.npcOutfit);
    },
    npcClothingSrc() {
      return this.outfitUrl(this.npcAvatar, this.npcOutfit.clothingType, this.npcOutfit.color);
    },
    npcAvatar() {
      return this.avatars.find(a => a.key === this.npcAvatarKey);
    },
  },
  watch: {
    avatarKey() {
      this.loadPatientImages();
    },
    'outfit.clothingType'() {
      this.loadPatientImages();
    },
    'outfit.color'() {
      this.loadPatientImages();
    },
    npcAvatarKey() {
      this.loadNpcImages();
    },
    npcOutfit() {
      this.loadNpcImages();
    },
  },
  mounted() {
    this.loadPatientImages();
    this.loadNpcImages();
  },
  methods: {
    layerOutfit(outfit) {
      if (!outfit) {
        return false;
      }
      switch (outfit.clothingType) {
        case 'MILITARY_OUTFIT_1':
        case 'POLICE_OUTFIT_1':
          return false;
        default:
          return true;
      }
    },
    environmentUrl(location) {
      return get(location, 'picture.url', 'http://icons.iconarchive.com/icons/papirus-team/papirus-status/512/image-missing-icon.png');
    },
    patientUrl(avatarKey, outfit) {
      if (outfit.clothingType === 'MILITARY_OUTFIT_1') {
        return `${process.env.VUE_APP_BASE_RESOURCE_URL}/avatars/fullBody/${avatarKey}-military.png`;
      } else if (outfit.clothingType === 'POLICE_OUTFIT_1') {
        return `${process.env.VUE_APP_BASE_RESOURCE_URL}/avatars/fullBody/${avatarKey}-police.png`;
      } else if (outfit.clothingType.includes('ISLAMIC')) {
        return `${process.env.VUE_APP_BASE_RESOURCE_URL}/avatars/fullBody/${avatarKey}-hijab.png`;
      } else {
        return `${process.env.VUE_APP_BASE_RESOURCE_URL}/avatars/fullBody/${avatarKey}.png`;
      }
    },
    outfitUrl(avatar, outfitKey, colorKey = null) {
      let url = `${process.env.VUE_APP_BASE_RESOURCE_URL}/avatars/fullBody/`;
      url += `${avatar.avatarOptions.bodyType}`;
      url += `-${avatar.avatarOptions.genderType}`;
      url += `-${outfitKey}`;
      if (outfitHasColor(outfitKey) && colorKey && colorKey !== 'WHITE') {
        url += `_x_${colorKey}`;
      }
      url += '.png';
      return url;
    },
    preloadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(src)
        img.onerror = reject
        img.src = src
      })
    },
    async loadPatientImages() {
      this.patientReady = false
      this.error = false
      try {
        // Start downloads concurrently & wait for both
        await Promise.all([
          this.preloadImage(this.patientClothingSrc),
          this.preloadImage(this.patientBaseSrc)
        ])
        // Only now render them together
        this.patientReady = true
      } catch (e) {
        // If either fails, render nothing (no nude)
        this.error = true
      }
    },
    async loadNpcImages() {
      this.npcReady = false
      this.error = false
      if (!this.npcAvatarKey) {
        return;
      }
      try {
        // Start downloads concurrently & wait for both
        await Promise.all([
          this.preloadImage(this.npcClothingSrc),
          this.preloadImage(this.npcBaseSrc)
        ])
        // Only now render them together
        this.npcReady = true
      } catch (e) {
        // If either fails, render nothing (no nude)
        this.error = true
      }
    },
  }
}
</script>

<style scoped>
.scene-image {
  aspect-ratio: 1.38 / 1;
  overflow: hidden;
  position: relative;
}

.environment-img, .npc-img, .patient-img, .outfit-img, .npc-outfit-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.npc-img, .patient-img, .outfit-img, .npc-outfit-img {
  background-color: rgba(0, 0, 0, 0.0);
}

.npc-img {
  z-index: 1;
}

.npc-outfit-img {
  z-index: 2;
}

.patient-img {
  z-index: 3;
}

.outfit-img {
  z-index: 4;
}

.centered {
  transform: translateX(0px) translateY(50px);
}
.left-shifted {
  transform: translateX(-100px) translateY(50px);
}
.right-shifted {
  transform: translateX(100px) translateY(50px);
}

</style>